<script setup>
import { ref, watch } from 'vue';

// Props 정의
const props = defineProps({
    modelValue: {
        type: String,
        default: ''
    },
    inputId: {
        type: String,
        default: ''
    },
    checkId: {
        type: String,
        default: ''
    },
    checkLabel: {
        type: String,
        default: ''
    },
    checked: {
        type: Boolean,
        default: false
    },
    label: {
        type: String,
        default: ''
    },
    required: {
        type: Boolean,
        default: false
    },
    placeholder: {
        type: String,
        default: ''
    },
    disabled: {
        type: Boolean,
        default: false
    },
    marginTop: {
        type: String,
        default: '0'
    },
    marginBottom: {
        type: String,
        default: '0'
    },
    marginLeft: {
        type: String,
        default: '0'
    },
    marginRight: {
        type: String,
        default: '0'
    },
    labelRightmargin: {
        type: String,
        default: '2rem'
    },
    visible: {
        type: Boolean,
        default: true
    },
    checkbox: {
        type: String,
        default: ''
    },
    maxlength: {
        type: Number,
        default: 4000
    },
    rows: {
        type: Number,
        default: 6
    },
    height: {
        type: String,
        default: '3rem'
    },
    validationCheck: {
        type: Function,
        default: () => ({ isValid: true, message: '' })
    },
    readonly: {
        type: Boolean,
        default: false
    },
    errmsg: {
        type: String,
        default: ''
    },
    labelEmpty: {
        type: Boolean,
        default: false
    },
    borderNone: {
        type: Boolean,
        default: false
    }
});

// Emits 정의
const emit = defineEmits(['update:modelValue', 'input', 'click', 'change']);
const isReadonly = ref(props.readonly);
//let selectionStart = event.target.selectionStart;
const inputValue = ref(props.modelValue);
const errMessage = ref(props.errmsg);
const isChecked = ref(props.checked);
const ValidationMsg = ref('');

watch(
    () => props.modelValue,
    (newVal) => {
        inputValue.value = newVal || '';
    },
    { immediate: true }
);

watch(
    () => props.errmsg,
    (newVal) => {
        ValidationMsg.value = newVal || '';
        errMessage.value = newVal || '';
    },
    { immediate: true, deep: true }
);

watch(
    () => props.checked,
    (newVal) => {
        isChecked.value = newVal;
    },
    { immediate: true, deep: true }
);

const onInput = (event) => {
    let value = event.target.value;
    let validationResult = { isValid: true, message: '' };
    ValidationMsg.value = '';
    errMessage.value = '';

    if (value.length > props.maxlength) {
        value = value.slice(0, props.maxlength);
    }

    ValidationMsg.value = validationResult.isValid ? '' : validationResult.message;

    // 값을 업데이트
    emitUpdate(value, 'input');

    // 값이 업데이트된 후 커서 위치 복원
    // nextTick(() => {
    //     event.target.setSelectionRange(selectionStart, selectionStart);
    // });
};

const emitUpdate = (value, event) => {
    inputValue.value = value;
    emit('update:modelValue', value);
    emit('input', {
        id: props.inputId,
        event: event,
        value: value,
        checked: isChecked.value,
        validation: ValidationMsg.value === '' ? true : false
    });
};

const onCheckboxClick = (event) => {
    if (!props.readonly) {
        isChecked.value = event.target.checked;
        emitUpdate(inputValue.value, 'check');
    }
};

const clearInput = () => {
    emitUpdate('', 'input');
};
</script>

<template>
    <div
        class="flex flex-col"
        :style="{
            marginTop: marginTop,
            marginBottom: marginBottom,
            marginLeft: marginLeft,
            marginRight: marginRight
        }"
    >
        <label v-if="!labelEmpty" class="label">
            {{ label }}
            <span v-if="required" class="required-asterisk">*</span>
            <span v-if="ValidationMsg" class="validation-message">{{ ValidationMsg }}</span>

            <!-- 체크박스가 라벨 오른쪽에 위치 -->
            <div v-if="checkId" class="checkbox-container">
                <label :for="checkId" class="checkbox-label">
                    {{ checkLabel }}
                </label>
                <input type="checkbox" :id="checkId" class="checkbox-input" :disabled="isReadonly" v-model="isChecked" @click="onCheckboxClick" />
            </div>
        </label>

        <div class="relative flex items-center w-full">
            <textarea
                v-if="visible"
                :id="inputId"
                v-model="inputValue"
                class="resize-none rounded-md block w-full text-gray-900 bg-gray-60 dark:bg-gray-700 dark:text-white"
                :class="{
                    'error-border': ValidationMsg,
                    'none-border': borderNone
                }"
                :style="{
                    height: height
                }"
                :rows="rows"
                :placeholder="placeholder"
                :required="required"
                :readonly="isReadonly"
                :maxlength="maxlength"
                @input="onInput"
            ></textarea>
            <div class="absolute right-2 top-1/2 transform -translate-y-1/2 flex items-center">
                <button v-if="inputValue && !isReadonly" @click="clearInput" class="clear-button">&times;</button>
            </div>
        </div>
    </div>
</template>

<style scoped>
.input-wrapper {
    display: flex;
    flex-direction: column;
}

.label {
    font-family: 'SCoreDream500';
    display: flex;
    /* justify-content: space-between; */
    align-items: center;
    margin-bottom: 0.25rem;
    margin-left: 0.25rem;
    height: 1.3rem;
    font-size: 1rem;
}

.required-asterisk {
    color: #f87171; /* 빨간색 */
    margin-left: 0.2rem;
    position: relative;
    top: -0.2rem;
}

textarea {
    font-family: 'SCoreDream400';
    font-size: 0.9rem;
    padding: 0.75rem;
    border: 0.1rem solid #d1d5db;
    border-radius: 0.5rem;
    resize: none; /* width와 height를 props로 제어할 예정이므로 resize 비활성화 */
}

textarea:focus {
    border-color: #0038ea;
    outline: none;
}

.error-border {
    border: 1px solid red !important;
}

.none-border {
    border: none;
}

.validation-message {
    color: #f87171;
    font-size: 0.75rem;
    margin-left: 0.5rem;
}

.checkbox-container {
    display: flex;
    align-items: center;
    margin-left: auto;
}

.checkbox-input {
    width: 1rem;
    height: 1rem;
    margin-right: 0.1rem;
    background-color: #f3f4f6;
    border: 0.1rem solid #d1d5db;
    border-radius: 0.25rem;
    cursor: pointer;
}

.checkbox-label {
    font-family: 'SCoreDream500';
    font-size: 0.85rem;
    margin-right: 0.5rem;
}

.clear-button {
    cursor: pointer;
    background: transparent;
    border: none;
    font-size: 1.4rem;
    color: #6b7280;
    padding: 0.25rem;
}

.clear-button:hover {
    color: #4b5563;
}
</style>
